import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';

interface ServerPublicKeyResponse {
  ServerPublicKey?: string;
  serverPublicKey?: string;
  Data?: string;
  data?: string;
}

interface EncryptionConfigResponse {
  Enabled?: boolean;
  enabled?: boolean;
  SkipEncryptionGetPaths?: string[];
  skipEncryptionGetPaths?: string[];
  Data?: EncryptionConfigData | string[];
  data?: EncryptionConfigData | string[];
}

interface EncryptionConfigData {
  Enabled?: boolean;
  enabled?: boolean;
  SkipEncryptionGetPaths?: string[];
  skipEncryptionGetPaths?: string[];
}

const DEFAULT_SKIP_ENCRYPTION_GET_PATHS: readonly string[] = [
  '/items/itemtype',
  '/items/hb',
  '/Items',
  '/warehouse',
  '/KegType',
  '/KegPackaging',
  '/KegProductType',
  '/KegStatus',
  '/GL',
  '/IO',
  '/brandsurvey',
  '/region',
  '/recommender',
];

export interface EncryptedRequestPayload {
  body: ArrayBuffer | null;
  headers: Record<string, string>;
  aesKey: CryptoKey;
}

@Injectable({ providedIn: 'root' })
export class EncryptionService {
  private readonly rawHttp: HttpClient;
  private serverPublicKeyPromise?: Promise<CryptoKey>;
  private configPromise?: Promise<void>;
  private serverEncryptionEnabled = true;
  private skipPaths: readonly string[] = DEFAULT_SKIP_ENCRYPTION_GET_PATHS;

  constructor(httpBackend: HttpBackend) {
    this.rawHttp = new HttpClient(httpBackend);
    if (this.isEnabled() && this.isSupported()) {
      this.getEncryptionConfig();
    }
  }

  isEnabled(): boolean {
    return (
      Boolean((environment as any).apiEncryption?.enabled) &&
      this.serverEncryptionEnabled
    );
  }

  async ensureConfigLoaded(): Promise<void> {
    if (Boolean((environment as any).apiEncryption?.enabled)) {
      await this.getEncryptionConfig();
    }
  }

  isSupported(): boolean {
    return typeof crypto !== 'undefined' && Boolean(crypto.subtle);
  }

  async encryptRequest(
    plaintext: ArrayBuffer | null,
  ): Promise<EncryptedRequestPayload> {
    // Fetch public key and encryption config in parallel on first call
    const [serverPublicKey] = await Promise.all([
      this.getServerPublicKey(),
      this.getEncryptionConfig(),
    ]);

    const keyPair = await crypto.subtle.generateKey(
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      ['deriveBits'],
    );

    const rawSharedSecret = await crypto.subtle.deriveBits(
      { name: 'ECDH', public: serverPublicKey },
      keyPair.privateKey,
      256,
    );
    const sharedSecret = await crypto.subtle.digest('SHA-256', rawSharedSecret);
    const aesKey = await crypto.subtle.importKey(
      'raw',
      sharedSecret,
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt'],
    );

    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const clientPublicKey = await crypto.subtle.exportKey(
      'spki',
      keyPair.publicKey,
    );

    const encryptedBody =
      plaintext && plaintext.byteLength > 0
        ? await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv: nonce },
            aesKey,
            plaintext,
          )
        : null;

    return {
      body: encryptedBody,
      aesKey,
      headers: {
        'X-Client-Public-Key': this.arrayBufferToBase64(clientPublicKey),
        'X-Nonce': this.arrayBufferToBase64(nonce),
        'X-Request-Id': this.createRequestId(),
        'X-Timestamp': Math.floor(Date.now() / 1000).toString(),
      },
    };
  }

  async decryptResponse(
    aesKey: CryptoKey,
    responseNonceBase64: string,
    encryptedBody: ArrayBuffer,
  ): Promise<ArrayBuffer> {
    const nonce = this.base64ToArrayBuffer(responseNonceBase64);
    return crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: new Uint8Array(nonce) },
      aesKey,
      encryptedBody,
    );
  }

  clearServerPublicKey(): void {
    this.serverPublicKeyPromise = undefined;
    this.configPromise = undefined; // re-fetch config alongside next public key
    this.serverEncryptionEnabled = true;
    this.skipPaths = [];
  }

  /**
   * Returns true if the given URL should NOT be encrypted on GET.
   * Driven by the server-fetched config (Option B — single source of truth).
   * Falls back to encrypting everything if config hasn't loaded yet.
   */
  isSkippedGetPath(url: string): boolean {
    return this.skipPaths.some((path) => url.includes(path));
  }

  private getServerPublicKey(): Promise<CryptoKey> {
    if (!this.serverPublicKeyPromise) {
      this.serverPublicKeyPromise = this.loadServerPublicKey();
    }
    return this.serverPublicKeyPromise;
  }

  private getEncryptionConfig(): Promise<void> {
    if (!this.configPromise) {
      this.configPromise = this.loadEncryptionConfig();
    }
    return this.configPromise;
  }

  private async loadServerPublicKey(): Promise<CryptoKey> {
    const publicKeyUrl = this.getPublicKeyUrl();
    const response = await this.rawHttp
      .get<ServerPublicKeyResponse>(publicKeyUrl)
      .toPromise();
    const publicKeyBase64 =
      response?.Data ||
      response?.data ||
      response?.ServerPublicKey ||
      response?.serverPublicKey;

    if (!publicKeyBase64) {
      throw new Error('Server public key response is missing ServerPublicKey.');
    }

    return crypto.subtle.importKey(
      'spki',
      this.base64ToArrayBuffer(publicKeyBase64),
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      [],
    );
  }

  private getPublicKeyUrl(): string {
    return `${environment.apiURL.replace(/\/$/, '')}/key-exchange/public-key`;
  }

  private getConfigUrl(): string {
    return `${environment.apiURL.replace(/\/$/, '')}/key-exchange/config`;
  }

  /**
   * Fetches the server-driven encryption config (Option B skiplist).
   * On any failure, falls back to an empty skiplist so all GET requests
   * are encrypted — secure-by-default on error.
   */
  private async loadEncryptionConfig(): Promise<void> {
    try {
      const response = await this.rawHttp
        .get<EncryptionConfigResponse>(this.getConfigUrl())
        .toPromise();

      const nestedConfig =
        response?.data && !Array.isArray(response.data)
          ? response.data
          : response?.Data && !Array.isArray(response.Data)
            ? response.Data
            : undefined;

      this.serverEncryptionEnabled =
        nestedConfig?.enabled ??
        nestedConfig?.Enabled ??
        response?.enabled ??
        response?.Enabled ??
        true;

      if (!this.serverEncryptionEnabled) {
        this.skipPaths = [];
        return;
      }

      const paths =
        nestedConfig?.skipEncryptionGetPaths ??
        nestedConfig?.SkipEncryptionGetPaths ??
        (Array.isArray(response?.data) ? response.data : undefined) ??
        (Array.isArray(response?.Data) ? response.Data : undefined) ??
        response?.SkipEncryptionGetPaths ??
        response?.skipEncryptionGetPaths ??
        [];

      this.skipPaths = paths.filter(
        (path): path is string => typeof path === 'string',
      );
    } catch {
      // Safe fallback: encrypt everything on config load failure
      this.skipPaths = [];
    }
  }

  private createRequestId(): string {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (value) =>
      (
        Number(value) ^
        (crypto.getRandomValues(new Uint8Array(1))[0] &
          (15 >> (Number(value) / 4)))
      ).toString(16),
    );
  }

  private arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
    const bytes =
      buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    let binary = '';

    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });

    return btoa(binary);
  }

  private base64ToArrayBuffer(value: string): ArrayBuffer {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }

    return bytes.buffer;
  }
}
